import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import { HasCartLineContext, withCartLine } from "@insite/client-framework/Components/CartLineContext";
import { Cart } from "@insite/client-framework/Services/CartService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import { CartLineModel, PromotionModel, QuoteModel } from "@insite/client-framework/Types/ApiModels";
import ProductBrand, { ProductBrandStyles } from "@insite/content-library/Components/ProductBrand";
import ProductDescription, { ProductDescriptionStyles } from "@insite/content-library/Components/ProductDescription";
import ProductImage, { ProductImageStyles } from "@insite/content-library/Components/ProductImage";
import ProductPartNumbers, { ProductPartNumbersStyles } from "@insite/content-library/Components/ProductPartNumbers";
import Icon from "@insite/mobius/Icon";
import CalifPropIconNew from "@MasterBluePrint/Icons/CalifPropIconNew";
import HazardousIcon from "@MasterBluePrint/Icons/Hazardous";
import RoHsIcon from "@MasterBluePrint/Icons/RoHs";
import ShippingIconCannotShipAir from "@MasterBluePrint/Icons/ShippingIconCannotShipAir";
import ShippingIconDryIce from "@MasterBluePrint/Icons/ShippingIconDryIce";
import ShippingIconNextDayAir from "@MasterBluePrint/Icons/ShippingIconNextDayAir";
import ProductPrice, { ProductPriceStyles } from "@insite/content-library/Components/ProductPrice";
import SmallHeadingAndText, { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import CartLineNotes, { CartLineNotesStyles } from "@insite/content-library/Widgets/Cart/CartLineNotes";
import CartLineQuantity, { CartLineQuantityStyles } from "@insite/content-library/Widgets/Cart/CartLineQuantity";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import { getQuote, GetQuoteApiParameter } from "@MasterBluePrint/Services/CustomQuoteService";
import React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    cart: Cart;
    promotions?: PromotionModel[];
    showSavingsAmount: boolean;
    showSavingsPercent: boolean;
    editable: boolean;
    showInvalidPriceMessage?: boolean;
    extendedStyles?: CartLineCardExpandedStyles;
}

const mapStateToProps = (state: ApplicationState) => ({
    enableVat: getSettingsCollection(state).productSettings.enableVat,
    vatPriceDisplay: getSettingsCollection(state).productSettings.vatPriceDisplay,
    currentPageUrl: getLocation(state).pathname
});

type Props = OwnProps & HasCartLineContext & ReturnType<typeof mapStateToProps>;

export interface CartLineCardExpandedStyles {
    cartLineNotes?: CartLineNotesStyles;
    price?: ProductPriceStyles;
    quantity?: CartLineQuantityStyles;
    container?: GridContainerProps;
    productImageGridItem?: GridItemProps;
    productImage?: ProductImageStyles;
    orderLineInfoGridItem?: GridItemProps;
    orderLineInfoContainer?: GridContainerProps;
    productDescriptionAndPartNumbersGridItem?: GridItemProps;
    productDescriptionAndPartNumbersContainer?: GridContainerProps;
    productBrand?: ProductBrandStyles;
    productBrandAndDescriptionGridItem?: GridItemProps;
    productDescription?: ProductDescriptionStyles;
    configurationGridItem?: GridItemProps;
    configurationOptionText?: TypographyProps;
    productPartNumbersGridItem?: GridItemProps;
    productPartNumbers?: ProductPartNumbersStyles;
    orderLineNotesGridItem?: GridItemProps;
    notesHeadingAndText?: SmallHeadingAndTextStyles;
    costCodeGridItem?: GridItemProps;
    costCodeValueText?: TypographyPresentationProps;
    costCodeLabelText?: TypographyPresentationProps;
    productPriceAndQuantityGridItem?: GridItemProps;
    productPriceAndQuantityContainer?: GridContainerProps;
    priceGridItem?: GridItemProps;
    priceHeadingAndText?: SmallHeadingAndTextStyles;
    promotionNameText?: TypographyProps;
    quantityGridItem?: GridItemProps;
    qtyHeadingAndText?: SmallHeadingAndTextStyles;
    extendedUnitNetPriceGridItem?: GridItemProps;
    extendedUnitNetPrice?: SmallHeadingAndTextStyles;
    subtotalWithoutVatText?: SmallHeadingAndTextStyles;
    vatAmount?: SmallHeadingAndTextStyles;
    iconGridItem?: GridItemProps;
    cartLineHazmatMessageGridItem?: GridItemProps;
    cartLineHazmatMessage?: TypographyPresentationProps;
    cartLineShipFreightGridItem?: GridItemProps;
    cartLineShipFreightMessage?: TypographyPresentationProps;
}

export const cartLineCardExpandedStyles: CartLineCardExpandedStyles = {
    container: {
        gap: 20,
        css: css`
            border-bottom: 1px solid #C2D1D9;
            padding: 1rem 0;

            &.expanded-view {
                > div {
                    width: calc(100% + 15px);
                }
                .price-container {
                    padding-top: .469rem;
                }

                .part-number-container {
                    order: 1;
                }
                .mfg-part-container {
                    order: 2;
                }
                .customer-part-container {
                    order: 3;
                }
            }
            
            @media (min-width: 768px) {
                p[data-test-selector="heading"] {
                    display: none;
                }
                .price-section {
                    legend {
                        display: none;
                    }
                }
            }

            @media (max-width: 767px) {
                p[data-test-selector="heading"] {
                   font-size: 1rem;
                   text-transform: capitalize;
                   display: inline-block;
                   margin-right: .625rem;
                   min-width: 8.9375rem;
                   &:after {
                        content: ":";
                    }
                }
                p[data-test-selector="text"] {
                    font-weight: normal;
                    display: inline-block;
                }
                .price-section {
                    flex-direction: row;
                    margin-bottom: 0;
                    legend {
                        font-size: 1rem;
                        text-transform: capitalize;
                        font-weight: 500;
                        display: inline-block;
                        margin-right: .625rem;

                        &:after {
                            content: ":";
                        }
                    }

                    span {
                        font-weight: normal;
                        display: inline-block;
                        margin-bottom: 0 !important;
                    }
                }
            }
        `,
    },
    productImageGridItem: {
        css: css`
            max-width: 8.125rem;
            flex-basis: 8.125rem;
            img {
                max-width: 6.9375rem;
                max-height: 6.9375rem;

                @media (max-width: 768px) {
                    max-width: 100%;
                }
            }
            @media print {
                max-height: 8.125rem;
                max-width: 8.125rem;
                width: 8.125rem;

                img {
                    max-width: 6.9375rem;
                    max-height: 6.9375rem;
                }
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        font-size: .875rem;
                        width: 100%;
                        justify-content: left;
                        flex-direction: column;
                        flex-basis: auto;
                        max-width: initial;
                        .mobile-product-heading {
                            margin-bottom: 1rem;
                            .brand-name {
                                font-size: .875rem;
                                letter-spacing: 0;
                                line-height: 1.25rem;
                            }
                            button {
                                color: #002554;
                                font-family: 'Roboto', sans-serif;
                                font-size: 1rem;
                                font-weight: 500;
                                letter-spacing: 0;
                                line-height: 1.25rem;

                                span {
                                    color: #002554;
                                    font-family: Roboto;
                                    font-size: 1rem; 
                                }
                            }
                        }
                        @media print {
                            width: auto;
                            min-width: 180px;
                            flex-direction: row;

                            .mobile-product-heading {
                                display: none;
                            }
                        }
                    `,
                    css`
                        font-size: .875rem;
                        width: 100%;
                        justify-content: left;
                        flex-direction: column;
                        flex-basis: auto;
                        max-width: initial;
                        .mobile-product-heading{
                            margin-bottom: 1rem;
                            .brand-name {
                                font-size: .875rem;
                                letter-spacing: 0;
                                line-height: 1.25rem;
                            }
                            button {
                                color: #002554;
                                font-family: 'Roboto', sans-serif;
                                font-size: 1rem;
                                font-weight: 500;
                                letter-spacing: 0;
                                line-height: 1.25rem;

                                span {
                                    color: #002554;
                                    font-family: Roboto;
                                    font-size: 1rem; 
                                }
                            }
                        }
                        @media print {
                            width: auto;
                            min-width: 180px;
                            flex-direction: row;

                            .mobile-product-heading {
                                display: none;
                            }
                        }
                    `,
                    css`
                        font-size: .875rem;
                        width: 100%;
                        justify-content: left;
                        flex-direction: column;
                        @media print {
                            width: auto;
                            min-width: 180px;
                            flex-direction: row;

                            .mobile-product-heading {
                                display: none;
                            }
                        }
                        .mobile-product-heading{
                            display: none;
                        }
                    `,
                    css`
                        font-size: .875rem;
                        .mobile-product-heading{
                            display: none;
                        }
                        @media print {
                            width: auto;
                            min-width: 180px;
                            flex-direction: row;

                            .mobile-product-heading {
                                display: none;
                            }
                        }
                    `,
                    css`
                        font-size: .875rem;
                        .mobile-product-heading{
                            display: none;
                        }
                        @media print {
                            width: auto;
                            min-width: 180px;
                            flex-direction: row;

                            .mobile-product-heading {
                                display: none;
                            }
                        }
                    `,
                ])}
        `,
    },
    orderLineInfoGridItem: {
        width: 10,
        css: css`
            flex-flow:row wrap;
            max-width: calc(100% - 134px);
            flex-basis: calc(100% - 134px);
            padding-right: 0 !important;

            @media screen and (min-width: 768px) and (max-width: 990px) {
                max-width: calc(100% - 148px);
                flex-basis: calc(100% - 148px);
            }

            @media screen and (max-width: 767px) {
                max-width: 100%;
                flex-basis: 100%;
            }

            @media print {
                max-width: calc(100% - 134px);
                flex-basis: calc(100% - 134px);
                width: calc(100% - 134px);
            }


            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            &.name-wrapper {
                                border-top: 1px solid #E1E1E1;

                                > div {
                                    max-width: 100%;
                                }
                                
                                @media print {
                                    border: none;
                                }
                            }
                            .desktop-product-heading {
                                display: none;
                            }
                        `,
                        null,
                        null,
                        null,
                    ],
                    "max",
                )}
        `,
    },
    orderLineInfoContainer: {
        gap: 15,
        css: css`
            > div {
                width: calc(100% + 15px) !important;
            }
        `,
    },
    productDescriptionAndPartNumbersGridItem: {
        width: [12, 12, 4, 4, 4],
    },
    productDescriptionAndPartNumbersContainer: {
        gap: 15,
    },
    productBrand: {
        nameText: {
            color: "text.main",
            css: css`
                color: #333F48;
                font-family: Roboto;
                font-size: .875rem;
                letter-spacing: 0;
                line-height: 1.5rem;
                text-transform: uppercase;
            `,
        },
    },
    productBrandAndDescriptionGridItem: {
        css: css`
            flex-direction: column;

            button {
                color: #002554 !important;
                font-family: Roboto;
                font-size: 1rem;
                font-weight: 500;
                letter-spacing: 0;
                line-height: 1.25rem;
                
                span {
                    color: #002554 !important;
                }

                &:hover,
                &:active,
                &:focus {
                    color: #002554;

                    span {
                        color: #002554;
                    }
                }

                @media print {
                    display: inline-flex !important;
                }
            }

            .brand-title {
                color: #333F48;
                font-family: Roboto;
                font-size: .875rem;
                letter-spacing: 0;
                line-height: 1.5rem;
                text-transform: uppercase;
            }
        `,
        width: 12,
    },
    configurationGridItem: {
        width: 12,
        css: css`
            flex-direction: column;
            padding-top: 0 !important;
        `,
    },
    productPartNumbersGridItem: {
        width: 12,
        css: css`
            padding-top: 0 !important;

            div {
                width: 100% !important;
                max-width: 100% !important;

                span {
                    display: inline-block !important;
                    padding-right: .31250625rem !important;

                    &:first-child {
                        color: #333F48;
                        font-family: Lato;
                        font-size: 1rem;
                        letter-spacing: 0;
                        line-height: 1.25rem;
                    }

                    &:last-child {
                        color: #333F48;
                        font-family: Lato;
                        font-size: 1rem;
                        letter-spacing: 0;
                        line-height: 1.25rem;
                    }
                }
            }
        `,
    },
    iconGridItem: {
        width: [6, 6, 12, 12, 12],
        css: css`
            background: #F7F9FA;
            padding: 3px 6px 9px;
            color: #333F48;
            font-family: 'Lato', sans-serif;
            font-size: .875rem;
            letter-spacing: 0;
            word-break:normal !important;
            @media screen and (min-width: 1400px) {
                flex-basis: 50%;
                max-width: 50%;
            }
            @media screen and (max-width: 1399px) {
                flex-basis: 100%;
                max-width: 100%;
            }	
            @media screen and (max-width: 768px) {
                flex-basis: 25%;
                max-width: 25%;
            }
            @media screen and (max-width: 600px) {
                flex-basis: 33%;
                max-width: 33%;
            }
            @media screen and (max-width: 470px) {
                flex-basis: 100%;
                max-width: 100%;
            }
        `,
    },
    orderLineNotesGridItem: {
        width: 12,
    },
    costCodeGridItem: { width: 12 },
    costCodeLabelText: {
        weight: "bold",
        css: css`
            margin-right: .625rem;
        `,
    },
    productPriceAndQuantityGridItem: {
        width: [12, 12, 8, 8, 8],
    },
    productPriceAndQuantityContainer: {
        gap: 15,
        css: css`
            > div {
                width: calc(100% + 15px) !important;
            }
        `,
    },
    priceGridItem: {
        width: [12, 12, 5, 5, 5],
        css: css`
            flex-direction: column;

            span {
                color: #333F48;
                font-size: 1rem;
                line-height: 1.5rem;
            }

            .vat-field {
                margin: 0;
                display: none;
            }
            
            @media screen and (max-width: 767px) {
                padding-top: 0;
                padding-bottom: 0;
            }
        `,
    },
    quantityGridItem: {
        width: [12, 12, 3, 3, 3],
        css: css`
            p {
                color: #333F48;
                font-size: 1rem;
                line-height: 1.5rem;
            }
            @media screen and (max-width: 767px) {
                > div > div {
                    > div:nth-child(2n) {
                        width: 100%;
                        > div > div {
                            flex-direction: row;
                            align-items: baseline;
                        }

                        p[data-test-selector="heading"] {
                            min-width: 2.5rem;
                        }
                    }
                }
                &.quantity-container {
                    padding-top: 0;
                    padding-bottom: 0;
                }    
            }
        `,
    },
    extendedUnitNetPriceGridItem: {
        width: [12, 12, 4, 4, 4],
        css: css`
            justify-content: end;
            text-align: right;
            padding-top: 0;

            @media screen and (max-width: 767px) {
                text-align: left;

                > div {
                    flex-direction: row;
                }
            }

            p {
                color: #333F48;
                font-size: 1rem;
                font-weight: bold;
                line-height: 1.5rem;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            flex-flow: wrap;
                        `,
                    ],
                    "max",
                )}
        `,
    },
    extendedUnitNetPrice: {
        text: {
            weight: 700,
        },
    },
    subtotalWithoutVatText: {
        wrapper: {
            css: css`
                margin-bottom: .3750625rem;
                width: 100%;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            null,
                            css`
                                width: 50%;
                            `,
                        ],
                        "max",
                    )}
            `,
        },
    },
    vatAmount: {
        wrapper: {
            css: css`
                margin-bottom: .3750625rem;
                width: 100%;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            null,
                            css`
                                width: 50%;
                            `,
                        ],
                        "max",
                    )}
            `,
        },
    },
    cartLineHazmatMessageGridItem: {
        width: 12,
        css: css`
            padding:5px 0 0;
        `,
    },
    cartLineHazmatMessage: {
        css: css`
            width:100%;
            background: #f9eae9;
            border:1px solid #df9088;
            border-left:3px solid #c63527;
            margin-bottom:5px;
            p {
                color: #222;
                font-size:85%;
                line-height:1.35;
                letter-spacing:.01rem;
                padding:3px 6px
            }
            
        `,
    },
    cartLineShipFreightGridItem: {
        width: 12,
        css: css`
            padding:5px 0 0;
        `,
    },
    cartLineShipFreightMessage: {
        css: css`
            width:100%;
            background: #e5f5fc;
            border:1px solid #72c8ed;
            border-left:3px solid #2060ad;
            margin-bottom:5px;
            p {
                color: #222;
                font-size:85%;
                line-height:1.35;
                letter-spacing:.01rem;
                padding:3px 6px
            }
            
        `,
    },
};

const CartLineCardExpanded = ({
    cart,
    cartLine,
    showSavingsAmount,
    showSavingsPercent,
    promotions,
    editable,
    enableVat,
    vatPriceDisplay,
    showInvalidPriceMessage,
    extendedStyles,
    currentPageUrl
}: Props) => {
    
    let shippingIconText = "";
    let isShowShippingIconText = true;
    let shippingIcon = ShippingIconNextDayAir;

    if (cartLine.properties?.shippingClassification === "MP") {
        shippingIconText = translate("Next Day Air Only");
        shippingIcon = ShippingIconNextDayAir;
    }

    else if (cartLine.properties?.shippingClassification?.toLowerCase() === "md") {
        shippingIconText = translate("Requires Dry Ice");
        shippingIcon = ShippingIconDryIce;
    }

    else if (
        cartLine.properties?.shippingClassification === "MG" ||
        cartLine.properties?.shippingClassification === "MC" ||
        cartLine.properties?.shippingClassification === "MB"
    ) {
        shippingIconText = translate("Cannot Ship Air");
        shippingIcon = ShippingIconCannotShipAir;
    }

    else {
        isShowShippingIconText = false;
    }
    const hazardousIconText = cartLine.properties?.isHazardousGood === "TRUE" ? translate("Hazardous") : "";
    const califPropIconText = translate("Calif. Prop 65");
    const roHsIconText = translate("RoHS");

    const [styles] = React.useState(() => mergeToNew(cartLineCardExpandedStyles, extendedStyles));
    const [quote, setQuote] = React.useState<QuoteModel | null>(null);
    const [quoteLine, setQuoteLine] = React.useState<CartLineModel | null>(null);
    const [isQuoteCart, setIsQuoteCart] = React.useState(false);
    React.useEffect(() => {
        if (cart) {
            if (cart.properties?.isquote === "true") {
                setIsQuoteCart(true);
                const parameter: GetQuoteApiParameter = { quoteId: cart.properties?.quoteNumber };
                getQuoteData(parameter).then(quote => {
                    setQuote(quote);
                });
            }
        }

    }, [cart]);
    React.useEffect(() => {
        if ((currentPageUrl.toLowerCase().includes("cart") || currentPageUrl.toLowerCase().includes("checkout") || currentPageUrl.toLowerCase().includes("orderconfirmation")) && cart && cart.properties?.isquote === "true") {
            const quoteLine = quote?.cartLines?.find(line => line.productId === cartLine.productId);
            if (quoteLine) {
                setQuoteLine(quoteLine);
            }
        }
    }, [cart, quote]);
    return (
        <GridContainer
            {...styles.container}
            data-test-selector={`cartline_expanded_${cartLine.productId}_${cartLine.unitOfMeasure}`}
            className="expanded-view" >
            <GridItem {...styles.productImageGridItem}>
                <Typography as="div" className="mobile-product-heading">
                    {cartLine.brand && (
                        <ProductBrand brand={cartLine.brand} extendedStyles={styles.productBrand} />
                    )}
                    <ProductDescription product={cartLine} extendedStyles={styles.productDescription} />
                </Typography>
                <ProductImage product={cartLine} extendedStyles={styles.productImage} />
            </GridItem>
            <GridItem {...styles.orderLineInfoGridItem}>
                <GridContainer {...styles.orderLineInfoContainer}>
                    <GridItem {...styles.productDescriptionAndPartNumbersGridItem} className="name-wrapper">
                        <GridContainer {...styles.productDescriptionAndPartNumbersContainer}>
                            <GridItem {...styles.productBrandAndDescriptionGridItem} className="desktop-product-heading">
                                {cartLine.brand && (
                                    <Typography as="span" className="brand-title">
                                        <ProductBrand brand={cartLine.brand} extendedStyles={styles.productBrand} />
                                    </Typography>
                                )}
                                <ProductDescription product={cartLine} extendedStyles={styles.productDescription} />
                            </GridItem>
                            {!cartLine.isFixedConfiguration && cartLine.sectionOptions!.length > 0 && (
                                <GridItem {...styles.configurationGridItem}>
                                    {cartLine.sectionOptions!.map(option => (
                                        <Typography {...styles.configurationOptionText} key={option.sectionOptionId}>
                                            {`${option.sectionName}:${option.optionName}`}
                                        </Typography>
                                    ))}
                                </GridItem>
                            )}
                            <GridItem {...styles.productPartNumbersGridItem}>
                                <ProductPartNumbers
                                    productNumber={cartLine.erpNumber}
                                    customerProductNumber={cartLine.customerName}
                                    manufacturerItem={cartLine.manufacturerItem}
                                    extendedStyles={styles.productPartNumbers}
                                />
                            </GridItem>
                            {cartLine.properties?.isHazardousGood === "TRUE" && (
                                <GridItem {...styles.iconGridItem}>
                                    <Icon
                                        title={hazardousIconText}
                                        src={HazardousIcon}
                                        css={css`
                                            margin-right: 0.25rem;
                                            width: 1.5rem !important;
                                            > svg {
                                                width: 1.5rem;
                                            }
                                        `}
                                    />
                                    {hazardousIconText}
                                </GridItem>
                            )}
                            {isShowShippingIconText && (
                                <GridItem {...styles.iconGridItem}>
                                    <Icon
                                        title={shippingIconText}
                                        src={shippingIcon}
                                        css={css`
                                            margin-right: 0.25rem;
                                            width: 1.5rem !important;
                                            > svg {
                                                width: 1.5rem;
                                            }
                                        `}
                                    />
                                    {shippingIconText}
                                </GridItem>
                            )}
                            {cartLine.properties?.californiaprop65 !== " " &&
                                cartLine.properties?.californiaprop65?.toLowerCase() === "y" && (
                                    <GridItem {...styles.iconGridItem}>
                                        <Icon
                                            src={CalifPropIconNew}
                                            css={css`
                                            margin-right: 0.25rem;
                                            width: 1.5rem !important;
                                            > svg {
                                                width: 1.5rem;
                                            }
                                        `}
                                        />
                                        {califPropIconText}
                                    </GridItem>
                                )}
                            {cartLine.properties?.roHS !== " " &&
                                cartLine.properties?.roHS?.toLowerCase() === "true" && (
                                    <GridItem {...styles.iconGridItem}>
                                        <Icon
                                            src={RoHsIcon}
                                            css={css`
                                            margin-right: 0.25rem;
                                            width: 1.5rem !important;
                                            > svg {
                                                width: 1.5rem;
                                            }
                                        `}
                                        />
                                        {roHsIconText}
                                    </GridItem>
                                )}
                        </GridContainer>
                    </GridItem>
                    <GridItem {...styles.productPriceAndQuantityGridItem}>
                        <GridContainer {...styles.productPriceAndQuantityContainer}>
                            <GridItem {...styles.priceGridItem} className="each-price-container">
                                <ProductPrice
                                    product={cartLine}
                                    showSavings={true}
                                    showSavingsAmount={showSavingsAmount}
                                    showSavingsPercent={showSavingsPercent}
                                    showInvalidPriceMessage={showInvalidPriceMessage}
                                    extendedStyles={styles.price}
                                />
                                {promotions?.map(promotion => (
                                    <Typography
                                        {...styles.promotionNameText}
                                        key={promotion.id}
                                        data-test-selector="orderLineCardExpandedPromotionName"
                                    >
                                        {promotion.name}
                                    </Typography>
                                ))}
                            </GridItem>
                            <GridItem {...styles.quantityGridItem} className="quantity-container">
                                <CartLineQuantity cart={cart} editable={editable} extendedStyles={styles.quantity} />
                            </GridItem>
                            <GridItem {...styles.extendedUnitNetPriceGridItem} className="price-container">
                                {isQuoteCart ? (
                                    quoteLine?.pricing && (
                                        <>
                                            {enableVat && vatPriceDisplay === "DisplayWithAndWithoutVat" && (
                                                <Typography as="div" className="subtotal-container">
                                                    <SmallHeadingAndText
                                                        heading={`${translate("Subtotal")} (${translate("Ex. VAT")})`}
                                                        text={quoteLine?.pricing.extendedUnitNetPriceDisplay}
                                                        extendedStyles={styles.subtotalWithoutVatText}
                                                    />
                                                    <SmallHeadingAndText
                                                        heading={`${translate("Total VAT")} (${quoteLine?.pricing.vatRate}%)`}
                                                        text={quoteLine?.pricing.vatAmountDisplay}
                                                        extendedStyles={styles.vatAmount}
                                                    />
                                                </Typography>
                                            )}
                                            <SmallHeadingAndText
                                                heading={
                                                    !enableVat ? `${translate("Subtotal")}` : vatPriceDisplay !== "DisplayWithoutVat" ? `${translate("Subtotal")} (${translate("Inc. VAT")})` : `${translate("Subtotal")} (${translate("Ex. VAT")})`
                                                }
                                                text={
                                                    enableVat && vatPriceDisplay !== "DisplayWithoutVat"
                                                        ? quoteLine?.pricing.extendedUnitRegularPriceWithVatDisplay
                                                        : quoteLine?.pricing.extendedUnitNetPriceDisplay
                                                }
                                                extendedStyles={styles.extendedUnitNetPrice}
                                            />
                                        </>
                                    )) : (
                                    cartLine.pricing && (
                                        <>
                                            {enableVat && vatPriceDisplay === "DisplayWithAndWithoutVat" && (
                                                <Typography as="div" className="subtotal-container">
                                                    <SmallHeadingAndText
                                                        heading={`${translate("Subtotal")} (${translate("Ex. VAT")})`}
                                                        text={cartLine.pricing.extendedUnitNetPriceDisplay}
                                                        extendedStyles={styles.subtotalWithoutVatText}
                                                    />
                                                    <SmallHeadingAndText
                                                        heading={`${translate("Total VAT")} (${cartLine.pricing.vatRate}%)`}
                                                        text={cartLine.pricing.vatAmountDisplay}
                                                        extendedStyles={styles.vatAmount}
                                                    />
                                                </Typography>
                                            )}
                                            <SmallHeadingAndText
                                                heading={
                                                    !enableVat ? `${translate("Subtotal")}` : vatPriceDisplay !== "DisplayWithoutVat" ? `${translate("Subtotal")} (${translate("Inc. VAT")})` : `${translate("Subtotal")} (${translate("Ex. VAT")})`
                                                }
                                                text={
                                                    enableVat && vatPriceDisplay !== "DisplayWithoutVat"
                                                        ? cartLine.pricing.extendedUnitRegularPriceWithVatDisplay
                                                        : cartLine.pricing.extendedUnitNetPriceDisplay
                                                }
                                                extendedStyles={styles.extendedUnitNetPrice}
                                            />
                                        </>
                                    ))}
                            </GridItem>
                        </GridContainer>
                    </GridItem>
                    {cartLine.properties?.isHazardousGood === "TRUE" && (
                        <GridItem {...styles.cartLineHazmatMessageGridItem}>
                            <Typography as="div" {...styles.cartLineHazmatMessage}>
                                <p><b>Warning:</b> This product contains hazardous materials (HazMat). Handle with care and avoid direct contact. Open in a well-ventilated area and do not store in airtight containers. Follow all applicable safety and carrier guidelines to ensure safe handling and compliance.</p>
                            </Typography>
                        </GridItem>
                    )}
                    {cartLine.properties?.shippingClassification === "ML" && (
                        <GridItem {...styles.cartLineShipFreightGridItem}>
                            <Typography as="div" {...styles.cartLineShipFreightMessage}>
                                <p><b>Important:</b> This product must ship via freight carrier due to its size, weight, or handling requirements. Additional transit time, delivery coordination, and shipping costs may apply.</p>
                            </Typography>
                        </GridItem>
                    )}
                    {cartLine.notes && (
                        <GridItem {...styles.orderLineNotesGridItem}>
                            <CartLineNotes
                                cart={cart}
                                editable={editable}
                                label={translate("Line Notes")}
                                extendedStyles={styles.cartLineNotes}
                            />
                        </GridItem>
                    )}
                    {cart.showCostCode && cartLine.costCode && (
                        <GridItem {...styles.costCodeGridItem}>
                            <Typography {...styles.costCodeValueText} as="p">
                                <Typography {...styles.costCodeLabelText}>{cart.costCodeLabel}:</Typography>
                                {cartLine.costCode}
                            </Typography>
                        </GridItem>
                    )}
                </GridContainer>
            </GridItem>
        </GridContainer>
    );
};
export async function getQuoteData(
    parameter: GetQuoteApiParameter,
): Promise<QuoteModel> {
    const quoteProduct = await getQuote(parameter);
    return quoteProduct;
}

export default connect(mapStateToProps)(withCartLine(CartLineCardExpanded));
